/**
 * AdminVoiceCloneInterest — Oct 2026
 *
 * Visual panel at /admin/voice-clone-interest. Every tester who taps
 * "Count me in" on the Clone My Voice card lands here, newest first,
 * with a short note, email, and a status chip (new / contacted /
 * built / declined). Lets Michael see demand build in real time and
 * reply to each person personally.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../AdminPages";
import { VoiceCloneUsagePanel } from "../../components/admin/VoiceCloneUsagePanel";
import {
  ArrowLeft, Mic, Loader2, Copy, Check, MessageCircle, Trash2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const STATUSES = ["new", "contacted", "built", "declined"];
const STATUS_STYLE = {
  new:       { bg: "bg-amber-50",   fg: "text-amber-700",   bd: "border-amber-200" },
  contacted: { bg: "bg-sky-50",     fg: "text-sky-700",     bd: "border-sky-200" },
  built:     { bg: "bg-emerald-50", fg: "text-emerald-700", bd: "border-emerald-200" },
  declined:  { bg: "bg-slate-100",  fg: "text-slate-600",   bd: "border-slate-300" },
};

export default function AdminVoiceCloneInterest() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, contacted: 0, built: 0, declined: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/voice-clone-interest`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(d.items || []);
      setCounts(d.counts || {});
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await fetch(`${API}/api/admin/voice-clone-interest/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ status }),
      });
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name || "this signal"}? Their account stays untouched.`)) return;
    try {
      await fetch(`${API}/api/admin/voice-clone-interest/${id}`, { method: "DELETE", headers: auth() });
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (_) { /* clipboard blocked — text visible on screen */ }
  };

  const whatsappMessage = (it) => {
    const first = (it.name || "").split(" ")[0] || "there";
    const noteHint = it.note
      ? `\n\nYou mentioned: "${it.note.slice(0, 80)}${it.note.length > 80 ? "…" : ""}"\n`
      : "";
    return `Hi ${first} — Michael here. Thank you for raising your hand for Clone My Voice.${noteHint}\n\nI'm opening it to a small first group soon. I'll send you a link to record your 90-second voice sample when it's ready. Any questions in the meantime, just ping me.\n\nMichael 🙏`;
  };

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => (it.status || "new") === filter);
  }, [items, filter]);

  const total = items.length;

  return (
    <AdminLayout title="Clone My Voice — interest signals">
      <VoiceCloneUsagePanel />
      <div className="mb-6 flex items-center justify-between" data-testid="admin-voice-clone">
        <button
          type="button"
          onClick={() => nav("/admin")}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
          data-testid="voice-clone-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </button>
        <button
          type="button"
          onClick={load}
          className="text-sm text-slate-500 hover:text-slate-900"
          data-testid="voice-clone-refresh"
        >
          Refresh
        </button>
      </div>

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-50 text-amber-700 p-2 flex-shrink-0">
            <Mic className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Demand signals so far</h2>
            <p className="text-sm text-slate-500 mt-1">
              Every tester who tapped <em>Count me in</em> on the Clone My Voice
              card lands here. When this list feels full enough to justify the
              build, flip the switch.
            </p>
          </div>
        </div>
      </div>

      {/* Status chips / filters */}
      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="voice-clone-filters">
        <FilterChip
          active={filter === "all"}
          onClick={() => setFilter("all")}
          label="All"
          count={total}
          testid="voice-clone-filter-all"
        />
        {STATUSES.map((s) => (
          <FilterChip
            key={s}
            active={filter === s}
            onClick={() => setFilter(s)}
            label={s.charAt(0).toUpperCase() + s.slice(1)}
            count={counts[s] || 0}
            testid={`voice-clone-filter-${s}`}
          />
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div className="text-sm text-red-600" data-testid="voice-clone-error">{err}</div>
      ) : filtered.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-sm text-slate-500 text-center"
          data-testid="voice-clone-empty"
        >
          {total === 0
            ? "No signals yet. The pitch card is live on /my-library — the first \"Count me in\" taps will land here."
            : `No signals with status "${filter}".`}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => {
            const style = STATUS_STYLE[it.status || "new"] || STATUS_STYLE.new;
            const waText = whatsappMessage(it);
            const waHref = `https://wa.me/?text=${encodeURIComponent(waText)}`;
            return (
              <div
                key={it.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3"
                data-testid={`voice-clone-row-${it.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {it.name || it.email}
                    </div>
                    <a
                      href={`mailto:${it.email}`}
                      className="text-sm text-sky-600 hover:underline truncate block"
                      data-testid={`voice-clone-email-${it.id}`}
                    >
                      {it.email}
                    </a>
                    <div className="text-xs text-slate-400 mt-1">
                      {new Date(it.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border ${style.bg} ${style.fg} ${style.bd} capitalize flex-shrink-0`}
                    data-testid={`voice-clone-status-${it.id}`}
                  >
                    {it.status || "new"}
                  </span>
                </div>

                {it.note && (
                  <blockquote
                    className="border-l-2 border-amber-300 pl-3 text-sm text-slate-600 italic"
                    data-testid={`voice-clone-note-${it.id}`}
                  >
                    "{it.note}"
                  </blockquote>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {STATUSES.filter((s) => s !== (it.status || "new")).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatus(it.id, s)}
                      className="text-xs px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 capitalize"
                      data-testid={`voice-clone-setstatus-${it.id}-${s}`}
                    >
                      → {s}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => copy(waText, `wa-${it.id}`)}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
                    data-testid={`voice-clone-copy-${it.id}`}
                  >
                    {copied === `wa-${it.id}`
                      ? <Check className="w-3.5 h-3.5 text-emerald-500" />
                      : <Copy className="w-3.5 h-3.5" />}
                    Copy msg
                  </button>
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid={`voice-clone-wa-${it.id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </a>
                  <button
                    type="button"
                    onClick={() => remove(it.id, it.name)}
                    className="ml-auto text-slate-400 hover:text-red-600"
                    aria-label="Remove"
                    data-testid={`voice-clone-remove-${it.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

function FilterChip({ active, onClick, label, count, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs border transition ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
      }`}
    >
      <span>{label}</span>
      <span
        className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
          active ? "bg-white/20" : "bg-slate-100 text-slate-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}
